import type { IFamily, IStudent, IStudentDraft } from 'src/interfaces/family'
import type { StudentInfoChange } from 'src/interfaces/client/infoChanges'
import { mergeFamilyPractitionersWithStudentDrafts } from './medicalPractitioners'
import { applyChangesForStudent } from 'src/utils/infoChange'

export function getAvatarColor(student: IStudent | IStudentDraft): string {
  // Generate color based on student name or ID
  const name = `${student.firstName || ''}${student.lastName || ''}`
  const colors = [
    '#667eea',
    '#764ba2',
    '#f093fb',
    '#4facfe',
    '#43e97b',
    '#fa709a',
    '#fee140',
    '#30cfd0',
  ]
  const index =
    name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) %
    colors.length
  return colors[index]
}

export function isDraftStudent(
  student: IStudent | IStudentDraft,
): student is IStudentDraft {
  return 'orignalId' in student
}

/**
 * Merges a student with their pending info changes (draft). The shared
 * student change applier also preserves immediately-saved immunization files.
 *
 * @param originalStudent - The original student data (can be undefined for new students)
 * @param infoChange - The pending StudentInfoChange (draft)
 * @returns The merged student with draft changes applied, or undefined if no valid data
 */
export function mergeStudentWithDraft(
  originalStudent: IStudent | undefined,
  infoChange: StudentInfoChange | undefined,
): IStudent | undefined {
  // Case 1: No student but has info change - NEW draft student awaiting approval
  if (!originalStudent && infoChange) {
    return {
      id: infoChange.id,
      familyId: infoChange.familyId,
      ...infoChange.changes,
    } as IStudent
  }

  // Case 2: Has both original and info change - merge changes onto original
  if (originalStudent && infoChange) {
    return applyChangesForStudent(originalStudent, infoChange.changes)
  }

  // Case 3: No draft changes, return original
  return originalStudent
}

export function mergeFamilyWithStudentDrafts(
  family: IFamily,
  studentInfoChanges: StudentInfoChange[] | undefined,
): IFamily {
  if (!studentInfoChanges?.length) return family

  const familyWithPractitioners = mergeFamilyPractitionersWithStudentDrafts(
    family,
    studentInfoChanges,
  )
  return {
    ...familyWithPractitioners,
    students: family.students.map(
      (student) =>
        mergeStudentWithDraft(
          student,
          studentInfoChanges.find((change) => change.id === student.id),
        ) ?? student,
    ),
  }
}
